import { useState, useEffect, useCallback } from "react";
import { API, useAuth } from "@/App";
import { logError } from "@/lib/logger";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Trash2,
  Plus,
  Edit,
  Save,
} from "lucide-react";

export const BannerManager = () => {
  const { token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [isActive, setIsActive] = useState(true);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchBanner = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/banner`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
      setIsActive(response.data.is_active !== false);
    } catch (error) {
      logError("Failed to load banner:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchBanner();
  }, [fetchBanner]);

  const saveBanner = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/banner?is_active=${isActive}`,
        messages,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      toast.success("Banner updated!");
    } catch (error) {
      logError("Banner save error:", error);
      toast.error("Failed to update banner");
    } finally {
      setSaving(false);
    }
  };

  const addMessage = () => {
    if (newMessage.trim()) {
      setMessages([...messages, newMessage.trim()]);
      setNewMessage("");
    }
  };

  const removeMessage = (index) => {
    setMessages(messages.filter((_, i) => i !== index));
  };

  const moveMessage = (index, direction) => {
    const newMessages = [...messages];
    const newIndex = index + direction;
    if (newIndex >= 0 && newIndex < messages.length) {
      [newMessages[index], newMessages[newIndex]] = [newMessages[newIndex], newMessages[index]];
      setMessages(newMessages);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="spinner" /></div>;

  return (
    <div data-testid="admin-banner-manager">
      <h1 className="font-heading text-3xl text-charcoal mb-2">Announcement Banner</h1>
      <p className="font-body text-stone-grey mb-8">Edit the scrolling banner messages shown at the top of your website</p>

      {/* Active Toggle */}
      <div className="glass rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-heading text-lg text-charcoal">Banner Status</h3>
            <p className="font-body text-sm text-stone-grey">Toggle whether the banner is visible to visitors</p>
          </div>
          <button
            onClick={() => setIsActive(!isActive)}
            className={`w-14 h-8 rounded-full transition-all ${isActive ? 'bg-jade-deep' : 'bg-stone-grey/30'}`}
          >
            <div className={`w-6 h-6 rounded-full bg-white shadow-md transform transition-transform ${isActive ? 'translate-x-7' : 'translate-x-1'}`} />
          </button>
        </div>
      </div>

      {/* Messages List */}
      <div className="glass rounded-xl p-6 mb-6">
        <h3 className="font-heading text-lg text-charcoal mb-4">Banner Messages</h3>
        
        {messages.length === 0 ? (
          <p className="font-body text-sm text-taupe mb-4">No messages yet. Add your first announcement below.</p>
        ) : (
          <div className="space-y-3 mb-6">
            {messages.map((msg, index) => (
              <div key={`${msg}-${index}`} className="flex items-center gap-3 bg-sand/30 rounded-lg p-3">
                <span className="font-body text-sm text-charcoal flex-1">{msg}</span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => moveMessage(index, -1)}
                    disabled={index === 0}
                    className="w-8 h-8 rounded flex items-center justify-center hover:bg-white disabled:opacity-30"
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => moveMessage(index, 1)}
                    disabled={index === messages.length - 1}
                    className="w-8 h-8 rounded flex items-center justify-center hover:bg-white disabled:opacity-30"
                  >
                    ↓
                  </button>
                  <button
                    onClick={() => removeMessage(index)}
                    className="w-8 h-8 rounded flex items-center justify-center hover:bg-lipstick-red/10"
                  >
                    <Trash2 className="w-4 h-4 text-lipstick-red" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add Message */}
        <div className="flex gap-3">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Enter a new banner message..."
            className="form-input flex-1"
            onKeyPress={(e) => e.key === 'Enter' && addMessage()}
          />
          <Button onClick={addMessage} className="btn-secondary">
            <Plus className="w-4 h-4 mr-2" /> Add
          </Button>
        </div>
      </div>

      {/* Save Button */}
      <Button onClick={saveBanner} disabled={saving} className="btn-primary">
        {saving ? "Saving..." : <><Save className="w-4 h-4 mr-2" /> Save Changes</>}
      </Button>
    </div>
  );
};

